import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { IDD_MIN_ESCALATION_DURATION_HOURS, IDD_MAX_ESCALATION_DURATION_HOURS } from 'src/app/constants/idd.constants';
import { TenantIddEscalationThresholdModel } from 'src/app/models/tenants/tenant-idd-escalation-threshold.model';

@Component({
  selector: 'app-idd-escalation-thresholds',
  standalone: false,
  templateUrl: './idd-escalation-thresholds.component.html',
  styleUrl: './idd-escalation-thresholds.component.scss',
})
export class IddEscalationThresholdsComponent {
  @ViewChild('iddEscalationGrid', { static: false })
  iddEscalationGrid!: DxDataGridComponent;

  @Input()
  isIddEscalationEnabled: boolean = true;

  @Input()
  iddEscalationThresholds: TenantIddEscalationThresholdModel[] = [];
  
  @Output() 
  iddEscalationThresholdsChange = new EventEmitter<TenantIddEscalationThresholdModel[]>();
  
  private readonly defaultIddEscalationThresholdMinutes = 60;
  minIddEscalationHours = IDD_MIN_ESCALATION_DURATION_HOURS;
  maxIddEscalationHours = IDD_MAX_ESCALATION_DURATION_HOURS;

  months = [
    { id: 1, name: this.translateService.instant('COMMON.MONTHS.JANUARY') },
    { id: 2, name: this.translateService.instant('COMMON.MONTHS.FEBRUARY') },
    { id: 3, name: this.translateService.instant('COMMON.MONTHS.MARCH') },
    { id: 4, name: this.translateService.instant('COMMON.MONTHS.APRIL') },
    { id: 5, name: this.translateService.instant('COMMON.MONTHS.MAY') },
    { id: 6, name: this.translateService.instant('COMMON.MONTHS.JUNE') },
    { id: 7, name: this.translateService.instant('COMMON.MONTHS.JULY') },
    { id: 8, name: this.translateService.instant('COMMON.MONTHS.AUGUST') },
    { id: 9, name: this.translateService.instant('COMMON.MONTHS.SEPTEMBER') },
    { id: 10, name: this.translateService.instant('COMMON.MONTHS.OCTOBER') },
    { id: 11, name: this.translateService.instant('COMMON.MONTHS.NOVEMBER') },
    { id: 12, name: this.translateService.instant('COMMON.MONTHS.DECEMBER') }
  ];

  constructor(
    private translateService: TranslateService
  ) {

  }

  onThresholdsChanged(): void {
    this.iddEscalationThresholdsChange.emit(
      this.iddEscalationThresholds
    );
  }

  isIddEscalationTimeValid(): boolean {
    if (!this.isIddEscalationEnabled) {
      return true;
    }

    const thresholds = this.iddEscalationThresholds;

    if (!thresholds?.length) {
      return false;
    }

    const minMinutes = this.minIddEscalationHours * 60;
    const maxMinutes = this.maxIddEscalationHours * 60;

    return thresholds.every(
      threshold =>
        threshold.thresholdMinutes != null &&
        threshold.thresholdMinutes >= minMinutes &&
        threshold.thresholdMinutes <= maxMinutes
    );
  }

  onIddEscalationThresholdAdding(e: any): void {
    const selectedMonths = this.iddEscalationThresholds
      .map(x => x.evaluationMonth);

    const maxSelectedMonth = Math.max(...selectedMonths, 0);

    const nextMonth = this.months.find(
      x => x.id > maxSelectedMonth && !selectedMonths.includes(x.id)
    );

    if (nextMonth) {
      e.data.evaluationMonth = nextMonth.id;
    }

    e.data.thresholdMinutes =
      this.defaultIddEscalationThresholdMinutes;
  }

  onEditorPreparing(e: any): void {
    if (
      e.parentType !== 'dataRow' ||
      e.dataField !== 'evaluationMonth'
    ) {
      return;
    }

    const currentMonth = e.value;

    const selectedMonths = this.iddEscalationThresholds
      .map(x => x.evaluationMonth)
      .filter(month => month !== currentMonth);

    e.editorOptions.dataSource = this.months.filter(
      month => !selectedMonths.includes(month.id)
    );
  }

  addIddEscalationThreshold(): void {
    this.iddEscalationGrid.instance.addRow();
  }

  getTimePickerValue(
    minutes: number
  ): { hours: number; minutes: number } {
    if (minutes == null) {
      return { hours: 0, minutes: 0 };
    }

    return {
      hours: Math.floor(minutes / 60),
      minutes: minutes % 60
    };
  }


  formatIddEscalationThreshold(minutes: number): string {
    if (minutes == null) {
      return '';
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0 && remainingMinutes === 0) {
      return '0 h';
    }

    return `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(
      2,
      '0'
    )} h`;
  }

  handleMonthlyIddEscalationTimePicker(
    cellInfo: any,
    value: { hours: number; minutes: number }
  ): void {
    const minutes =
      value.hours * 60 + value.minutes;

    cellInfo.setValue(minutes);
  }

  scrollIntoView(): void {
    this.iddEscalationGrid?.instance
      ?.element()
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
  }
}
